import type { GooglePlace, GooglePlaceFields } from "@albertine/types";
import { AiCapturedBadge } from "@prototypes/ai-capture/AiCapturedBadge";
import classNames from "classnames";
import React, {
    type Ref,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { googlePlaceService } from "../../common/utils/google/GooglePlaceService";
import config from "../../config";
import { useGoogleMapsScript } from "../../useScript";
import Icon from "../Common/Icon";
import "./GooglePlaceInputField.css";

type UseGooglePlaceInputRefProps = {
    initialValue: string | undefined;
};

export const useGooglePlaceInputRef = (props: UseGooglePlaceInputRefProps) => {
    const { initialValue } = props;

    const googlePlaceInputField = useRef<{
        input?: () => HTMLInputElement | null;
        clear: () => void | null;
    }>(null);

    useEffect(() => {
        if (initialValue && googlePlaceInputField.current?.input) {
            googlePlaceInputField.current.input()?.setAttribute("value", initialValue);
        }
    }, [initialValue]);

    return googlePlaceInputField;
};

type GooglePlaceInputFieldProperties = {
    autoFocus?: boolean;
    label?: string | undefined;
    googlePlaceId?: string | undefined;
    placeholder?: string | undefined;
    fields: GooglePlaceFields;
    displayLocationPin?: boolean;
    className?: string;
    searchFor?: keyof typeof PLACE_TYPES;
    readonlyWhenPlaceSelected?: boolean;
    highlight?: boolean;
    onSelect: (place: GooglePlace) => void;
    onClear?: () => void;
    value?: string | undefined;
};

const GooglePlaceInputField = React.forwardRef(
    (
        props: GooglePlaceInputFieldProperties,
        refs: Ref<{
            map?: () => HTMLDivElement | null;
            input?: () => HTMLInputElement | null;
            clear?: () => void | null;
        }>,
    ) => {
        const {
            autoFocus,
            label,
            googlePlaceId,
            placeholder,
            fields,
            displayLocationPin,
            className,
            searchFor,
            readonlyWhenPlaceSelected = true,
            highlight,
            onClear,
            onSelect,
            value,
        } = props;
        const [place, setPlace] = useState<GooglePlace>();
        const [hasListener, setHasListener] = useState(false);

        const mapRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        const scriptLoadStatus = useGoogleMapsScript();

        const googleService = googlePlaceService(config.mapsApiKey);

        const clear = () => {
            if (inputRef.current) {
                inputRef.current.value = "";
            }
            setPlace(undefined);
            if (onClear) onClear();

            inputRef.current?.focus();
        };

        useImperativeHandle(refs, () => ({
            map: () => mapRef.current,
            input: () => inputRef.current,
            clear,
        }));

        useEffect(() => {
            if (place) onSelect(place);
        }, [place]);

        useEffect(() => {
            if (scriptLoadStatus !== "ready") return;

            async function getGooglePlaceData(placeId: string | undefined) {
                if (!placeId || !config.mapsApiKey) return;
                const googlePlace = await googleService.fetchGooglePlace(
                    placeId,
                    fields,
                );
                setPlace(googlePlace);
            }

            if (window.google && !hasListener && inputRef.current) {
                setHasListener(true);

                if (!inputRef.current) return;

                const autoComplete = new window.google.maps.places.Autocomplete(
                    inputRef.current,
                );
                autoComplete.setFields(["place_id"]);
                autoComplete.addListener("place_changed", () => {
                    const newPlace = autoComplete.getPlace();
                    getGooglePlaceData(newPlace.place_id);
                });

                if (searchFor) {
                    autoComplete.setOptions({
                        types: PLACE_TYPES[searchFor],
                    });
                }
            }
        }, [scriptLoadStatus, onSelect, fields, hasListener, searchFor, googleService]);

        useEffect(() => {
            if (googlePlaceId && inputRef.current) {
                inputRef.current?.setAttribute("value", googlePlaceId);
            }
        }, [googlePlaceId]);

        useEffect(() => {
            if (value && inputRef.current) {
                inputRef.current.value = value;
            }
        }, [value]);

        return (
            <div
                className={classNames(
                    "google-place-input",
                    className,
                    highlight && "!border-blue-200 !bg-blue-50",
                )}
            >
                <div className={`${className || "google-place"}__input`}>
                    {label && <span className="google-place__label">{label}</span>}
                    <div
                        className={classNames(
                            "flex min-w-0 items-center",
                            highlight && "pr-1",
                        )}
                    >
                        <input
                            autoFocus={autoFocus}
                            ref={inputRef}
                            placeholder={placeholder}
                            readOnly={readonlyWhenPlaceSelected && !!place}
                            className={classNames(
                                "min-w-0 flex-1",
                                highlight && "!bg-transparent",
                            )}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    const pressArrowDown = new KeyboardEvent(
                                        "keydown",
                                        {
                                            keyCode: 40,
                                        },
                                    );
                                    const pressEnter = new KeyboardEvent("keydown", {
                                        keyCode: 13,
                                    });
                                    event.currentTarget.dispatchEvent(pressArrowDown);
                                    event.currentTarget.dispatchEvent(pressEnter);
                                    event.preventDefault();
                                }
                            }}
                        />
                        {inputRef.current?.value && (
                            <button
                                type="button"
                                className={classNames(
                                    "flex shrink-0 items-center justify-center",
                                    !highlight && "pr-2",
                                )}
                                onClick={clear}
                            >
                                <Icon.Close.Small className="google-place__close-icon" />
                            </button>
                        )}
                        {highlight && <AiCapturedBadge inline />}
                        {displayLocationPin && !inputRef.current?.value && (
                            <Icon.LocationOn.Small className="mr-2 shrink-0" />
                        )}
                    </div>
                    <div ref={mapRef} />
                </div>
            </div>
        );
    },
);

const PLACE_TYPES = {
    politicalRegions: ["political"],
    landmarksAndRegions: ["political", "point_of_interest"],
    citiesOnly: ["locality", "administrative_area_level_3"],
    citiesAndCountriesOnly: ["country", "locality", "administrative_area_level_3"],
};

export default GooglePlaceInputField;
